import {
  Dialog,
  Button,
  Box,
  Input,
  Stack,
  Text,
  NumberInput,
  Checkbox,
  VStack,
  HStack,
  Badge
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { apiPost, apiPut } from '../api';

// Simple Alert replacement since useToast is missing
const alertToast = (title, status) => {
  window.alert(`${status === 'error' ? 'Error' : 'Success'}: ${title}`);
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const PaymentModal = ({ isOpen, onClose, article, onPaymentSuccess, paymentToEdit }) => {
  const [paymentType, setPaymentType] = useState('interest');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // State for loan selection (indices of the loans array)
  const [selectedLoanIndices, setSelectedLoanIndices] = useState([]);

  // Initialize selection when modal opens or article changes
  useEffect(() => {
    if (isOpen) {
      if (paymentToEdit) {
        setPaymentType(paymentToEdit.type);
        setAmount(paymentToEdit.amount);
        setDate(paymentToEdit.date ? new Date(paymentToEdit.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
        setMonths(paymentToEdit.months || 1);
        // Default to all loans selected if editing, or maintain existing logic if we stored loan indices (we don't currently)
        if (article?.loans) {
             setSelectedLoanIndices(article.loans.map((_, i) => i));
        }
      } else if (article?.loans) {
        // Reset for new payment
        setPaymentType('interest');
        setAmount('');
        setDate(new Date().toISOString().split('T')[0]);
        setMonths(1);
        setSelectedLoanIndices(article.loans.map((_, i) => i));
      }
    }
  }, [isOpen, article, paymentToEdit]);

  // Calculate estimated interest based on selected loans
  const calculateEstimatedInterest = () => {
    if (!article || !article.loans) return 0;
    
    let totalInterest = 0;
    
    selectedLoanIndices.forEach(index => {
      const loan = article.loans[index];
      if (!loan) return;
      
      const principal = Number(loan.amount) || 0;
      // Use loan-specific rate if available, else fallback to article global rate
      const rate = Number(loan.interestRate || article.interestRate || 0);
      
      // Formula: Principal * (Rate / 100) * (Months / 12)
      // Note: This assumes simple interest per loan segment for the given months
      const interest = principal * (rate / 100) * (months / 12);
      totalInterest += interest;
    });
    
    return Math.round(totalInterest);
  };

  const estimatedInterest = calculateEstimatedInterest();

  // Suggest "interest paid upto" from months (matches backend reports logic)
  useEffect(() => {
    if (!paymentToEdit && paymentType === 'interest' && article && months > 0) {
      const loans = article.loans || [];
      let base = article.interestPaidUpto ? new Date(article.interestPaidUpto) : null;
      if (!base && loans.length) {
        const sorted = [...loans].sort((a, b) => new Date(a.date) - new Date(b.date));
        base = new Date(sorted[0].date);
      } else if (!base && article.date) {
        base = new Date(article.date);
      }
      if (base) {
        const d = new Date(base);
        d.setMonth(d.getMonth() + Number(months));
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        setDate(`${y}-${m}-${day}`);
      }
    }
  }, [months, paymentType, article, paymentToEdit]);

  // Auto-fill amount when factors change (only for Interest type)
  useEffect(() => {
    // Only auto-calc if NOT editing an existing payment (user might have custom amount)
    // Or if user explicitly changes months/loans in edit mode, maybe we should update?
    // Let's only auto-calc for NEW payments or if user changes params in edit mode significantly.
    // Simpler: If paymentToEdit is present, don't overwrite amount unless user changes months?
    // Actually, `estimatedInterest` depends on `months`. If `months` changes, we probably want to update amount.
    // But `amount` is state. 
    
    // Logic: If NOT editing, auto-fill.
    // If Editing, only auto-fill if we assume user wants recalc.
    // Let's stick to: Auto-fill only if NOT editing OR if user changes months manually (hard to track diff).
    // Safest: Only auto-fill if !paymentToEdit
    if (!paymentToEdit && paymentType === 'interest' && article) {
      setAmount(estimatedInterest);
    }
  }, [months, paymentType, article, selectedLoanIndices, estimatedInterest, paymentToEdit]); 

  const handleToggleLoan = (index, checked) => {
    setSelectedLoanIndices(prev => {
      if (checked === false) { // Uncheck
        return prev.filter(i => i !== index);
      } else { // Check (true or indeterminate)
        // Avoid duplicates just in case
        return prev.includes(index) ? prev : [...prev, index];
      }
    });
  };

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      alertToast('Invalid amount', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: paymentType,
        amount: Number(amount),
        date,
        months: paymentType === 'interest' ? Number(months) : null,
        description: paymentType === 'interest' 
          ? `Interest Payment (${months} months)` 
          : 'Principal Repayment'
      };

      if (paymentToEdit) {
        await apiPut(`/articles/${article._id}/payment/${paymentToEdit._id}`, payload);
      } else {
        await apiPost(`/articles/${article._id}/payment`, payload);
      }
      
      alertToast(paymentToEdit ? 'Payment Updated' : 'Payment Recorded', 'success');
      onPaymentSuccess();
      onClose();
    } catch (err) {
      alertToast(err.message || 'Payment failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!article) return null;

  return (
    <Dialog.Root 
      open={isOpen} 
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      size="md"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>{paymentToEdit ? 'Edit Payment' : 'Make Payment'}</Dialog.Title>
            <Dialog.CloseTrigger onClick={onClose} />
          </Dialog.Header>
          <Dialog.Body>
            <Stack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Article: <b>{article.nameOrNo}</b> | Total Balance: ₹{article.totalPrincipal || article.amount}
              </Text>

              <Box>
                <Text fontSize="sm" mb={1}>Payment Type</Text>
                <select 
                   value={paymentType} 
                   onChange={(e) => setPaymentType(e.target.value)}
                   style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                >
                  <option value="interest">Interest Payment</option>
                  <option value="principal">Principal Repayment</option>
                </select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  {paymentType === 'interest' ? 'Interest paid upto (date)' : 'Payment date'}
                </Text>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                {paymentType === 'interest' && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Auto-updates when you change months. Reports use this to reduce pending interest.
                  </Text>
                )}
              </Box>

              {paymentType === 'interest' && (
                <>
                  <Box>
                    <Text fontSize="sm" mb={1} fontWeight="medium">Select Loans to Calculate Interest</Text>
                    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2} maxH="150px" overflowY="auto" bg="gray.50">
                      <VStack align="stretch" spacing={2}>
                        {article.loans && article.loans.length > 0 ? (
                            article.loans.map((loan, index) => (
                                <HStack key={index} justify="space-between" bg="white" p={2} borderRadius="sm" shadow="sm">
                                    <Checkbox.Root 
                                        checked={selectedLoanIndices.includes(index)}
                                        onCheckedChange={(e) => handleToggleLoan(index, e.checked)}
                                    >
                                        <Checkbox.HiddenInput />
                                        <Checkbox.Control>
                                            <Checkbox.Indicator />
                                        </Checkbox.Control>
                                        <Checkbox.Label fontSize="sm" ml={2}>
                                            {loan.description || (index === 0 ? 'Initial' : 'Top-up')} 
                                        </Checkbox.Label>
                                    </Checkbox.Root>
                                    <HStack>
                                       <Badge colorScheme="blue">₹{loan.amount}</Badge>
                                       <Text fontSize="xs" color="gray.500">
                                            {formatDate(loan.date)}
                                       </Text>
                                    </HStack>
                                </HStack>
                            ))
                        ) : (
                            <Text fontSize="xs" color="gray.500">No detailed loan history available.</Text>
                        )}
                      </VStack>
                    </Box>
                  </Box>

                  <Box>
                    <Text fontSize="sm" mb={1}>Number of Months</Text>
                    <NumberInput.Root min={0.1} value={months} onValueChange={(e) => setMonths(Number(e.value))}>
                      <NumberInput.Input />
                      <NumberInput.Control>
                        <NumberInput.IncrementTrigger />
                        <NumberInput.DecrementTrigger />
                      </NumberInput.Control>
                    </NumberInput.Root>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Calculated Interest: <b>₹{estimatedInterest}</b>
                    </Text>
                  </Box>
                </>
              )}

              <Box>
                <Text fontSize="sm" mb={1}>Amount to Pay (₹)</Text>
                <NumberInput.Root min={0} value={amount} onValueChange={(e) => setAmount(Number(e.value))}>
                  <NumberInput.Input />
                </NumberInput.Root>
              </Box>
            </Stack>
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={handleSubmit} isLoading={loading}>
              {paymentToEdit ? 'Update Payment' : 'Confirm Payment'}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default PaymentModal;
